import _ from "lodash";
/** External */
import React from "react";
import { Tooltip } from "@toddle-edu/ds-web";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import ACLStore from "lib/aclStore";

/** Internal */
import {
  ADD_ON_MANAGER,
  AUTH_AND_EVAL,
  FETCH_SNP_SET_WITH_CYCLE_STATUS,
  LIBRARY,
  MANAGE_CURRICULUM_PERMISSION,
  MANAGE_GRADES_PERMISSION,
} from "InternalTools/modules/InternalToolConstants";

export const getSubTextFromCurriculumProgramType = curriculumType => {
  const curriculumSubTextMap = {
    IB_PYP: "PYP",
    IB_DP: "DP",
    IB_MYP: "MYP",
    UBD: "UBD",
  };

  return _.get(curriculumSubTextMap, curriculumType, curriculumType);
};

export const getSnpEnabledCurriculumPrograms = curriculumPrograms => {
  return _.filter(
    curriculumPrograms,
    curriculumProgram => curriculumProgram.snpEnabled
  );
};

export const getPermissionsPayloadForMutation = curriculumPrograms => {
  return _.map(curriculumPrograms, curriculumProgram => ({
    entityGroups: [
      [
        {
          entityId: curriculumProgram.id,
          entityType: "CURRICULUM_PROGRAM",
        },
      ],
    ],
    value: curriculumProgram.snpEnabled.toString(),
    valueType: "BOOLEAN",
  }));
};

export const getSnpSetWithCycleStatusQueryVariables = ({
  organizationId,
  curriculumUids,
}) => {
  return {
    input: {
      apiType: ADD_ON_MANAGER,
      params: {
        organizationId,
        addOnType: AUTH_AND_EVAL,
        queryType: FETCH_SNP_SET_WITH_CYCLE_STATUS,
        curriculumUids,
      },
    },
  };
};

export const bulkAttendanceUploadVisible = () => {
  return ACLStore.can("Common:BulkUploadAttendance");
};

export const bulkUnitPlanUploadVisible = () => {
  return ACLStore.can("IdentityAccess:BulkUploadUnitPlanPdf");
};

export const bulkCalendarUploadVisible = () => {
  return ACLStore.can("IdentityAccess:BulkUploadSchoolCalendar");
};

export const sesDeleteSuppressedEmailToolVisible = () => {
  return ACLStore.can(
    "SesDeleteSuppressedDestinationCommandAccess:InternalTools"
  );
};

export const npsSurveyVisible = () => {
  return true;
};

export const enableWorkrampManager = () => {
  return ACLStore.can("IdentityAccess:EditWorkrampAcademyList");
};

export const getPermissionsPayloadForLibraryMutation = curriculumPrograms => {
  return _.map(curriculumPrograms, curriculumProgram => ({
    curriculumProgramId: curriculumProgram.id,
    curriculumProgramUid: curriculumProgram.uid,
    value: curriculumProgram.libraryEnabled,
  }));
};

export const getLibraryGradesQueryVariables = ({
  organizationId,
  curriculumProgramIds,
}) => {
  return {
    input: {
      apiType: "ADD_ON_MANAGER",
      params: {
        organizationId,
        addOnType: LIBRARY,
        queryType: "FETCH_LIBRARY_PORTAL_PREMIUM_GRADE_STATUS",
        curriculumProgramIds,
      },
    },
  };
};

export const getLibraryPortalActivationStatusQueryVariables = organizationId => ({
  input: {
    apiType: "ADD_ON_MANAGER",
    params: {
      organizationId,
      addOnType: "LIBRARY",
      queryType: "FETCH_LIBRARY_PORTAL_ACTIVATION_STATUS",
    },
  },
});

export const getLibraryPortalActivationStatusQueryData = ({
  curriculumPrograms,
}) => ({
  platform: {
    internalToolsGenericQuery: {
      data: {
        curriculumPrograms,
      },
      isSuccess: true,
      warning: null,
      __typename: "InternalToolsGenericApiResponse",
    },
    __typename: "PlatformQuery",
  },
});

export const getLibraryTabOptions = hasEnabledCurriculums => {
  return [
    {
      value: MANAGE_CURRICULUM_PERMISSION,
      label: "Enable library for curriculum programs",
    },
    {
      value: MANAGE_GRADES_PERMISSION,
      label: "Make resources premium for grades",
      disabled: !hasEnabledCurriculums,
      suffix: !hasEnabledCurriculums ? (
        <Tooltip tooltip="Please enable library for at least one curriculum program to continue">
          <InformationOutlined />
        </Tooltip>
      ) : null,
    },
  ];
};

export const getChangedGrades = (
  originalGrades,
  updatedGrades,
  shouldGetAddedGrades = true
) => {
  const originalGradesObj = _.keyBy(originalGrades, "gradeUid");

  return _.reduce(
    updatedGrades,
    (result, grade) => {
      const isGradePermissionChanged =
        _.get(originalGradesObj, `${grade.gradeUid}.isLibraryPremium`) !==
        grade.isLibraryPremium;

      if (
        isGradePermissionChanged &&
        shouldGetAddedGrades === grade.isLibraryPremium
      )
        result.push(grade.gradeUid);

      return result;
    },
    []
  );
};

export const getGradesWithDpEnabled = grades => {
  return _.map(grades, grade =>
    grade.curriculumUid === "IB_DP"
      ? { ...grade, isLibraryPremium: true }
      : grade
  );
};

export const attachmentDropDownOptions = [
  { key: "upload_device", label: "upload_device" },
  { key: "add_google_drive", label: "common:add_google_drive" },
];

const UNIT_TYPE_LABEL_MAP = {
  ibInquiry: "Transdisciplinary",
  ibStandAlone: "Subject specific",
  ibMYPSubjectSpecific: "Subject specific",
  ibMYPInterdisciplinary: "Interdisciplinary",
  ubdInquiry: "Interdisciplinary",
  ubdSubjectSpecific: "Subject specific",
};

const plannerFormatArray = ["PYP", "E_PYP"];

export const getTemplateSubtitle = ({ unitType, plannerFormat }) => {
  if (!_.isEmpty(unitType)) {
    return UNIT_TYPE_LABEL_MAP[unitType];
  } else if (_.includes(plannerFormatArray, plannerFormat)) {
    return "Transdisciplinary / Subject specific";
  } else {
    return "Interdisciplinary / Subject specific";
  }
};

export const parseBulkUploadErrors = ({ errorData }) => {
  return _.map(errorData, ({ error, row, uid }) => ({
    row_number: row,
    error,
    uid,
  }));
};

export const parseBulkCalendarUploadErrors = ({ errorData }) => {
  return _.map(errorData, ({ error, row }) => ({
    row_number: row,
    error,
  }));
};

export const betaProductRequestVisible = () => {
  return ACLStore.can("IdentityAccess:EnableProductRequestView");
};

export const lrtDashboardVisible = () => {
  return ACLStore.can("IdentityAccess:LrtDashboardView");
};

export const importUnitPlanUsingAIVisible = () => {
  return ACLStore.can("IdentityAccess:ImportUnitPlanUsingAI");
};

export const dataMigrationToolkitVisible = () => {
  return ACLStore.can("IdentityAccess:DataMigrationToolkit");
};

export const bulkImportActivityVisible = () => {
  return ACLStore.can("IdentityAccess:BulkImportActivity");
};
